import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import {
  Search, Building, Filter, UserRound, Star, Users, Briefcase, BookmarkPlus, BookmarkCheck, MessageSquare, Video, Calendar, Send, Clock, Check, CreditCard, X, Hourglass, Lock,
  Crown, Eye, Zap
} from "lucide-react";
import { getDashboardAnalytics, addSellerRecommendationStatus, checkSubscriptionStatus, createBuyerDemo, getCompanySizes, searchBuyerDirectory, getBuyerIndustries, getSeniorityLevels, addBuyerRecommendationStatus, removeSellerRecommendationStatus, addDemoMessages } from '../../utils/api';
import useSubscriberStatus from "../../hooks/useSubscriberStatus";
import UserProfile from "../userprofile/UserProfile";

const UpgradeDialog = ({ isOpen, onClose }) => {
  const navigate = useNavigate();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
      <div className="bg-white rounded-lg w-full max-w-md mx-4 p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Upgrade Required</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-gray-600 mb-6">
          Upgrade your account to invite potential buyers to product demos. This feature is available only for premium subscribers.
        </p>
        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-700 font-medium"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              // TODO: Add upgrade flow navigation
              navigate('/subscription');
              onClose();
            }}
            className="px-4 py-2 bg-purple-500 text-white rounded-md hover:bg-purple-600"
          >
            Upgrade Now
          </button>
        </div>
      </div>
    </div>
  );
};

// ConnectBuyerDialog component now directly in BuyerDirectory.tsx and converted to JavaScript
function ConnectBuyerDialog({ open, onOpenChange, buyer, setBuyerData }) {
  const [connectOption, setConnectOption] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState("");

  // Mock user credits - in a real app, this would come from your authentication system
  const USER_CREDITS = 5;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      let res = await addSellerRecommendationStatus(buyer.registered_user_id, 'Approved', 'Buyer Directory');

      if (res.success) {
        let data2 = { buyer_id: buyer.registered_user_id, credit_cost: 5, message: 'Buyer Directory' }
        let res2 = await createBuyerDemo(data2);

        let refid = res2.data;

        if (message.length > 0)
          await addDemoMessages(refid, message);

        // set buyer demo_pending to true 
        setBuyerData(prev => prev.map(b => b.registered_user_id === buyer.registered_user_id ? { ...b, request_sent: true } : b));

        setSubmitted(true);

      }

    } catch (error) {
      console.error('Failed to send intro:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md relative overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {/* Close button */}
        <button
          onClick={() => {
            setSubmitted(false);
            onOpenChange(false);
            setConnectOption(null);
            setMessage("");
          }}
          className="absolute right-4 top-4 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Close"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>

        {/* Header */}
        <div className="p-6 border-b border-gray-100">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Connect with {buyer.first_name} {buyer.last_name}</h2>
              <p className="text-sm text-gray-500">
                {buyer.job_title} at {buyer.company_name}
              </p>
            </div>

            {/* <div className="flex items-center bg-purple-50 px-3 py-1.5 rounded-full">
              <CreditCard className="h-4 w-4 text-purple-600 mr-1.5" />
              <span className="text-sm font-medium text-purple-700">{USER_CREDITS} credits left</span>
            </div> */}
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {submitted ? (
            <div className="py-6 flex flex-col items-center justify-center text-center">
              <div className="bg-green-100 rounded-full p-3 mb-4">
                <Check className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-lg font-medium">Request sent!</h3>
              <p className="text-sm text-gray-500 mt-2">
                {connectOption === "message"
                  ? "Your message has been sent. You'll receive a notification when they respond."
                  : "Demo request sent. You'll receive a calendar invite once they accept."}
              </p>
              {/* 
              <p className="text-xs text-purple-600 mt-4 flex items-center">
                <CreditCard className="h-3.5 w-3.5 mr-1" />
                1 credit used, {USER_CREDITS - 1} credits remaining
              </p> */}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-4 py-4">

                <div
                  className={`border rounded-lg p-4 text-center cursor-pointer transition-all ${connectOption === "demo"
                    ? "border-purple-500 bg-purple-50"
                    : "hover:border-gray-400"
                    }`}
                  onClick={() => setConnectOption("demo")}
                >
                  <Video className="h-8 w-8 mx-auto mb-2 text-purple-600" />
                  <h3 className="font-medium">Schedule Demo</h3>
                  <p className="text-xs text-gray-500 mt-1">
                    Invite to product demo
                  </p>

                  {/* <span className="inline-flex items-center rounded-full border border-purple-200 bg-purple-50 px-2.5 py-0.5 text-xs font-semibold text-purple-700 mt-2">
                    1 credit
                  </span> */}
                </div>
              </div>

              {connectOption && (
                <>
                  {connectOption === "message" ? (
                    <>
                      <div className="space-y-4">
                        <div>
                          <label className="text-sm font-medium">Subject</label>
                          <input
                            type="text"
                            placeholder="e.g., Introducing our solution"
                            className="mt-1 w-full rounded-md border-gray-300 border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            required
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium">Message</label>
                          <textarea
                            placeholder="Write your message here..."
                            className="w-full min-h-[80px] rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent mt-1"
                            rows={3}
                            required
                          ></textarea>
                          <p className="text-xs text-gray-500 mt-1 flex items-center">
                            <Clock className="h-3 w-3 mr-1" />
                            Average response time: 2 days
                          </p>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="space-y-4">

                        <div>
                          <label className="text-sm font-medium">Message (optional)</label>
                          <textarea
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            placeholder="Add any details about your product that might interest them..."
                            className="w-full min-h-[80px] rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent mt-1"
                            rows={2}
                          ></textarea>
                        </div>
                        <div className="flex items-center py-2 text-sm text-gray-500">
                          <Calendar className="h-4 w-4 mr-2 text-purple-600" />
                          <span>They'll select available times after accepting</span>
                        </div>
                      </div>
                    </>
                  )}

                  <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2">
                    <button
                      type="button"
                      onClick={() => setConnectOption(null)}
                      disabled={isSubmitting}
                      className="mt-3 sm:mt-0 px-4 py-2 rounded-md border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50"
                    >
                      Back
                    </button>
                    <button
                      type="submit"
                      onClick={(e) => handleSubmit(e)}
                      disabled={isSubmitting}
                      className="px-4 py-2 rounded-md border border-transparent font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 text-sm inline-flex items-center disabled:opacity-50"
                    >
                      {isSubmitting ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="h-4 w-4 mr-2" />
                          {connectOption === "message" ? "Send Message" : "Send Demo Request"}
                        </>
                      )}
                    </button>
                  </div>
                </>
              )}
            </>
          )}

          {/* {!submitted && !connectOption && (
            <div className="text-xs text-gray-500 mt-2 text-center">
              <p className="flex items-center justify-center">
                <CreditCard className="h-3.5 w-3.5 mr-1" />
                Connecting with buyers costs 1 credit per request
              </p>
            </div>
          )} */}
        </div>
      </div>
    </div>
  );
}

const BuyerDirectory = () => {
  const ITEMS_PER_PAGE = 10;
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIndustry, setSelectedIndustry] = useState(null);
  const [selectedCompanySizes, setSelectedCompanySizes] = useState([]);
  const [selectedSeniorityLevels, setSelectedSeniorityLevels] = useState([]);
  const [tempCompanySizes, setTempCompanySizes] = useState([]);
  const [tempIndustry, setTempIndustry] = useState(null);
  const [bookmarkedBuyers, setBookmarkedBuyers] = useState([]);
  const [showBookmarkedOnly, setShowBookmarkedOnly] = useState(false);
  const [selectedBuyer, setSelectedBuyer] = useState(null);
  const [connectDialogOpen, setConnectDialogOpen] = useState(false);
  const [buyerData, setBuyerData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [hasSubscription, setHasSubscription] = useState(false);
  const [isUpgradeDialogOpen, setIsUpgradeDialogOpen] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [companySizes, setCompanySizes] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [seniorityLevels, setSeniorityLevels] = useState([]);
  const [selectedIndustries, setSelectedIndustries] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadedSubscriptionCheck, setLoadedSubscriptionCheck] = useState(false);
  const [isFiltering, setIsFiltering] = useState(false);
  const [totalBookmarked, setTotalBookmarked] = useState(0);
  const [showUserProfile, setShowUserProfile] = useState(false);
  const [selectedSlug, setSelectedSlug] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const imageUrl = process.env.REACT_APP_BASE_IMAGE_URL;
  const { subscriberStatus, subscriberLoading, subscriberData, subscriberError, refetch } = useSubscriberStatus();

  const fetchBuyers = async () => {
    try {
      // Fetch buyers from API or local data
      const response = await searchBuyerDirectory(currentPage, searchQuery, selectedIndustries, selectedCompanySizes, selectedSeniorityLevels, showBookmarkedOnly);
      setBuyerData(response.data);
      setTotalCount(response.total); // Assuming response.total contains the total count of buyers
      setTotalPages(Math.ceil(response.total / ITEMS_PER_PAGE)); // Calculate total pages based on items per page
      setTotalBookmarked(response.total_bookmarked); // Assuming response.total_bookmarked contains the total count of bookmarked buyers

    } catch (error) {
      console.error("Error fetching buyers:", error);
    } finally {
      setLoading(false);
    }
  };

      async function handleLoadProfile(slug, id) {
      setSelectedSlug(slug);
      setSelectedUserId(id);
      setShowUserProfile(true);
    }

  useEffect(() => {
    const fetchSeniorityLevels = async () => {
      try {
        const response = await getSeniorityLevels();
        setSeniorityLevels(response.data);
      } catch (error) {
        console.error("Error fetching seniority levels:", error);
      }
    };

    const fetchIndustries = async () => {
      try {
        const response = await getBuyerIndustries();
        setIndustries(response.data);
      } catch (error) {
        console.error("Error fetching industries:", error);
      }
    };

    const fetchCompanySizes = async () => {
      try {
        const response = await getCompanySizes();
        setCompanySizes(response.data);
      } catch (error) {
        console.error("Error fetching company sizes:", error);
      }
    };

    async function loadSubscriptionCheck() {
      let res = await checkSubscriptionStatus();

      if (res.success) {
        setHasSubscription(res.data);
        setLoadedSubscriptionCheck(true);
      }
    }

    const fetchBuyers = async () => {
      try {
        // Fetch buyers from API or local data
        const response = await searchBuyerDirectory(currentPage);
        setBuyerData(response.data);
        setTotalCount(response.total); // Assuming response.total contains the total count of buyers
        setTotalPages(Math.ceil(response.total / ITEMS_PER_PAGE)); // Calculate total pages based on items per page
        setTotalBookmarked(response.total_bookmarked); // Assuming response.total_bookmarked contains the total count of bookmarked buyers

      } catch (error) {
        console.error("Error fetching buyers:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSeniorityLevels();
    fetchIndustries();
    fetchCompanySizes();
    loadSubscriptionCheck();
    fetchBuyers();
  }, []);

  // Load bookmarked buyers from localStorage on component mount
  useEffect(() => {
    const savedBookmarks = localStorage.getItem('bookmarkedBuyers');
    if (savedBookmarks) {
      setBookmarkedBuyers(JSON.parse(savedBookmarks));
    }
  }, []);

  const handlePageChange = async (page) => {
    // if (!hasSubscription && page > 1) {
    //   setIsUpgradeDialogOpen(true);
    //   return;
    // }

    let res = await searchBuyerDirectory(page, searchQuery, selectedIndustries, selectedCompanySizes, selectedSeniorityLevels, showBookmarkedOnly);

    if (res.success) {
      setBuyerData(res.data);
      setCurrentPage(page);
      setTotalBookmarked(res.total_bookmarked); // Assuming response.total_bookmarked contains the total count of bookmarked buyers
      // Scroll to top after page change
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const renderPaginationButtons = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`px-3 py-1 mx-1 rounded ${currentPage === i
            ? 'bg-purple-600 text-white'
            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
        >
          {i}
        </button>
      );
    }

    return (
      <div className="flex items-center justify-center space-x-2">
        <button
          onClick={() => handlePageChange(1)}
          disabled={currentPage === 1}
          className="px-3 py-1 rounded bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50"
        >
          First
        </button>
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="px-3 py-1 rounded bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50"
        >
          Previous
        </button>
        {pages}
        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="px-3 py-1 rounded bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50"
        >
          Next
        </button>
        <button
          onClick={() => handlePageChange(totalPages)}
          disabled={currentPage === totalPages}
          className="px-3 py-1 rounded bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50"
        >
          Last
        </button>
      </div>
    );
  };

  // Add apply filters function
  const applyFilters = async () => {
    setIsFiltering(true);
    try {
      await fetchBuyers();
    } finally {
      setIsFiltering(false);
    }
  };

  // Add toggle function for company sizes
  const toggleCompanySize = (size) => {
    console.log("Toggling company size:", size);

    if (selectedCompanySizes.includes(size.company_size_id)) {
      setSelectedCompanySizes(prev => prev.filter(id => id != size.company_size_id));
    }
    else {
      setSelectedCompanySizes(prev => [...prev, size.company_size_id]);
    }
  };

  // Add toggle function for industries
  const toggleIndustry = (industry) => {
    console.log("Toggling industry:", industry);

    if (selectedIndustries.includes(industry.industry_id)) {
      setSelectedIndustries(prev => prev.filter(id => id !== industry.industry_id));
    } else {
      setSelectedIndustries(prev => [...prev, industry.industry_id]);
    }
  };

  // Add toggle function for seniority levels
  const toggleSeniorityLevel = (level) => {
    console.log("Toggling seniority level:", level);

    if (selectedSeniorityLevels.includes(level.seniority_level_id)) {
      setSelectedSeniorityLevels(prev => prev.filter(id => id !== level.seniority_level_id));
    } else {
      setSelectedSeniorityLevels(prev => [...prev, level.seniority_level_id]);
    }
  };

  // Filter buyers based on search query, filters, and bookmarks
  const filteredBuyers = buyerData.filter(buyer => {
    const matchesSearch =
      buyer.first_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      buyer.company_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (buyer.headline || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (buyer.industry || '').toLowerCase().includes(searchQuery.toLowerCase());

    const matchesIndustry = selectedIndustries.length === 0 ||
      selectedIndustries.includes(buyer.industry_id);
    const matchesSize = selectedCompanySizes.length === 0 ||
      selectedCompanySizes.includes(buyer.company_size_id);
    const matchesSeniority = selectedSeniorityLevels.length === 0 ||
      selectedSeniorityLevels.includes(buyer.seniority_level_id);
    const matchesBookmark = true;

    return matchesSearch && matchesIndustry && matchesSize && matchesSeniority && matchesBookmark;
  });

  // Function to display initials for avatar fallback
  const getInitials = (name) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase();
  };

  // Add new state after other state declarations
  const [bookmarkLoading, setBookmarkLoading] = useState(null);

  // Handle bookmark toggling
  const handleBookmarkToggle = async (buyerId, is_bookmarked) => {
    setBookmarkLoading(buyerId);
    try {
      if (is_bookmarked == false) {
        let res = await addSellerRecommendationStatus(buyerId, 'Bookmark', 'Buyer Directory');
        if (res.success) {
          setBookmarkedBuyers(prev => [...prev, buyerId]);
          setBuyerData(prev => prev.map(buyer => buyer.registered_user_id === buyerId ? { ...buyer, is_bookmarked: true } : buyer));
          setTotalBookmarked(prev => prev + 1);
        }
      } else {
        let res = await removeSellerRecommendationStatus(buyerId, 'Bookmark');
        if (res.success) {
          setBookmarkedBuyers(prev => prev.filter(id => id !== buyerId));
          setTotalBookmarked(prev => prev - 1);
          setBuyerData(prev => prev.map(buyer => buyer.registered_user_id === buyerId ? { ...buyer, is_bookmarked: false } : buyer));
        }
      }
    } finally {
      setBookmarkLoading(null);
    }
  };

  // Handle connect button click
  const handleConnectClick = (buyer) => {
    if (!hasSubscription) {
      setIsUpgradeDialogOpen(true);
      return;
    }
    setSelectedBuyer(buyer);
    setConnectDialogOpen(true);
  };

  // Reset all filters
  const resetFilters = () => {
    setSelectedIndustries([]);
    setSelectedCompanySizes([]);
    setSelectedSeniorityLevels([]);
    setSearchQuery("");
    setShowBookmarkedOnly(false);
  };

  // Modified display for the saved buyers filter
  const savedBuyersCount = bookmarkedBuyers.length;

  // Add new handler after other handler functions
  const handleSearchKeyPress = async (e) => {
    if (e.key === 'Enter') {
      await applyFilters();
    }
  };

  const renderExclusiveBuyerCard = (buyer) => (
    <div
      key={buyer.id}
      className="bg-gradient-to-br from-amber-50 via-white to-purple-50 rounded-lg shadow-md hover:shadow-xl transition-all duration-300 border-2 border-amber-200 overflow-hidden relative transform hover:-translate-y-1"
    >
      {/* Exclusive badge */}
      <div className="absolute top-0 right-0 bg-gradient-to-r from-amber-400 to-yellow-500 text-white px-3 py-1 rounded-bl-lg shadow-md">
        <div className="flex items-center gap-1">
          <Crown className="h-3 w-3" />
          <span className="text-xs font-semibold">EXCLUSIVE</span>
        </div>
      </div>

      {/* Bookmark button - positioned at the top-right */}
      <button
        className={`absolute top-2 right-2 backdrop-blur-sm hover:bg-white z-10 rounded-full shadow-sm p-2 
                        ${buyer.is_bookmarked
            ? 'bg-purple-100 text-purple-600'
            : 'bg-white/80 text-gray-500'}`}
        onClick={() => handleBookmarkToggle(buyer.registered_user_id, buyer.is_bookmarked)}
        disabled={bookmarkLoading === buyer.registered_user_id}
      >
        {bookmarkLoading === buyer.registered_user_id ? (
          <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        ) : buyer.is_bookmarked ? (
          <BookmarkCheck className="h-5 w-5" />
        ) : (
          <BookmarkPlus className="h-5 w-5" />
        )}
      </button>

      {/* Profile image at the top */}
      <div className="w-full bg-gradient-to-b from-amber-100 to-white pt-6 pb-3 px-6 flex justify-center">
        <div className="h-20 w-20 rounded-full border-4 border-amber-300 shadow-lg overflow-hidden flex items-center justify-center bg-amber-100 text-amber-700 relative">
          {buyer.profileImg ? (
            <img
              src={buyer.profileImg}
              alt={buyer.name}
              className="h-full w-full object-cover"
            />
          ) : (
            <span className="text-xl font-medium">
              {getInitials(buyer.first_name)}
            </span>
          )}
          <div className="absolute -bottom-1 -right-1 bg-amber-500 rounded-full p-1">
            <Crown className="h-3 w-3 text-white" />
          </div>
        </div>
      </div>

      <div className="p-5">
        <div className="text-center mb-4">
          <div className="flex items-center justify-center gap-1.5 mb-1">
            <h3 className="font-semibold text-lg text-gray-900">{buyer.name}</h3>
            {buyer.verified && (
              <div className="inline-flex">
                {/* Using a custom verified badge display instead of component */}
                <div className="flex">
                  {buyer.verified === 'email' && (
                    <span className="bg-blue-100 text-blue-800 text-xs px-1.5 py-0.5 rounded-full">Email</span>
                  )}
                  {buyer.verified === 'linkedin' && (
                    <span className="bg-blue-100 text-blue-800 text-xs px-1.5 py-0.5 rounded-full">LinkedIn</span>
                  )}
                  {buyer.verified === 'both' && (
                    <div className="flex gap-1">
                      <span className="bg-blue-100 text-blue-800 text-xs px-1.5 py-0.5 rounded-full">Verified</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="text-gray-600 text-sm font-medium">
            <span>{buyer.job_title}</span>
          </div>

          <div className="flex items-center justify-center text-gray-500 text-sm mt-1">
            <Building className="h-3.5 w-3.5 mr-1" />
            <span>{buyer.company_name}</span>
            <span className="mx-1.5 text-gray-300">•</span>
            <span>{buyer.company_size}</span>
          </div>
        </div>

        <div className="mt-4">
          <p className="text-gray-700 text-sm">{buyer.headline}</p>
        </div>

        <div className="mt-4">
          <div className="flex flex-wrap gap-1.5 justify-center">
            <span className="inline-flex items-center bg-amber-100 text-amber-800 hover:bg-amber-200 text-xs font-semibold px-2.5 py-0.5 rounded-full border border-amber-300">
              {buyer.industry}
            </span>
          </div>
        </div>

        <div className="mt-5 pt-4 border-t border-amber-200">
          <div className="flex justify-between items-center">
            <div className="space-x-1 text-xs text-gray-600">
              <span>{buyer.country_name}</span>
            </div>

            <div className="flex gap-2">

              <button
                className="bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium py-1.5 px-3 rounded-md shadow-md flex items-center"
                onClick={() => handleConnectClick(buyer)}
              >
                <Zap className="h-3.5 w-3.5 mr-1" />
                Connect
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-gradient-to-b from-purple-800 to-purple-600 text-white py-16">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div>
              <h1 className="text-4xl font-bold mb-4 flex items-center">
                <Users className="mr-3 h-8 w-8" />
                Software Buyer Directory
              </h1>
              <p className="text-purple-100 text-lg max-w-xl">
                Connect with qualified buyers actively looking for software solutions like yours.
              </p>
            </div>

          </div>
        </div>
      </div>

      <div className=" mx-auto px-4 py-8 max-w-7xl">
        <div className="flex flex-col md:flex-row gap-6">
          {/* Filter sidebar */}
          <div className="w-full md:w-64 space-y-6 md:sticky md:top-4 md:h-[calc(100vh-2rem)] flex flex-col">
            <div className="bg-white rounded-lg shadow-sm p-5 space-y-4 flex-1 overflow-auto">

              <div className="mt-6 md:mt-0 w-full md:w-auto">
                <div className="relative w-full">
                  <input
                    type="text"
                    placeholder="Search buyers..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={handleSearchKeyPress}
                    className="bg-white text-black pl-4 pr-4 py-2 h-12 rounded-lg w-full border border-transparent focus:border-purple-300 focus:ring focus:ring-purple-200 focus:ring-opacity-50 outline-none"
                  />
                  <Search className="absolute right-3 top-3 h-5 w-5 text-gray-400" />
                </div>
              </div>

              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-gray-800 flex items-center">
                  <Filter className="mr-2 h-4 w-4" /> Filters
                </h3>
              </div>

              <div className="space-y-4">
                {/* Saved filter - updated with count */}
                <div>
                  <div className="flex items-center mb-2">
                    <input
                      type="checkbox"
                      id="saved-filter"
                      checked={showBookmarkedOnly}
                      onChange={() => setShowBookmarkedOnly(!showBookmarkedOnly)}
                      className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                    />
                    <label htmlFor="saved-filter" className="ml-2 text-sm font-medium text-gray-700 flex items-center">
                      Saved Buyers <BookmarkCheck className="ml-1.5 h-4 w-4 text-purple-600" />
                      {totalBookmarked > 0 && (
                        <span className="ml-2 text-xs bg-purple-100 text-purple-700 px-2.5 py-0.5 rounded-full font-semibold">
                          {totalBookmarked}
                        </span>
                      )}
                    </label>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">Industry</label>
                  <div className="space-y-2">
                    {industries.map(industry => (
                      <div key={industry.industry_id} className="flex items-center">
                        <input
                          type="checkbox"
                          id={`industry-${industry.industry_id}`}
                          checked={selectedIndustries.includes(industry.industry_id)}
                          onChange={() => toggleIndustry(industry)}
                          className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                        />
                        <label htmlFor={`industry-${industry.industry_id}`} className="ml-2 text-sm text-gray-700">
                          {industry.name}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">Seniority Level</label>
                  <div className="space-y-2">
                    {seniorityLevels.map(level => (
                      <div key={level.seniority_level_id} className="flex items-center">
                        <input
                          type="checkbox"
                          id={`seniority-${level.seniority_level_id}`}
                          checked={selectedSeniorityLevels.includes(level.seniority_level_id)}
                          onChange={() => toggleSeniorityLevel(level)}
                          className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                        />
                        <label htmlFor={`seniority-${level.seniority_level_id}`} className="ml-2 text-sm text-gray-700">
                          {level.name}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">Company Size</label>
                  <div className="space-y-2">
                    {companySizes.map(size => (
                      <div key={size.company_size_id} className="relative flex items-start">
                        <div className="flex items-center h-5">
                          <input
                            type="checkbox"
                            id={`size-${size.company_size_id}`}
                            checked={selectedCompanySizes.includes(size.company_size_id)}
                            onChange={() => toggleCompanySize(size)}
                            className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                          />
                        </div>
                        <label htmlFor={`size-${size.company_size_id}`} className="ml-2 text-sm text-gray-700">
                          {size.value}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Sticky footer with apply filters button */}
            <div className="bg-white rounded-lg shadow-sm p-4 border-t border-gray-100">
              <div className="flex gap-2">
                <button
                  onClick={applyFilters}
                  disabled={isFiltering}
                  className="flex-1 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium py-2 px-4 rounded-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {isFiltering ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Applying...
                    </>
                  ) : (
                    'Apply Filters'
                  )}
                </button>
                {(selectedIndustries.length > 0 || selectedCompanySizes.length > 0 || selectedSeniorityLevels.length > 0 || showBookmarkedOnly) && (
                  <button
                    onClick={resetFilters}
                    disabled={isFiltering}
                    className="text-sm px-4 py-2 text-gray-600 hover:text-gray-800 font-medium border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Reset
                  </button>
                )}
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-5 border border-purple-200">
              <h3 className="font-semibold text-purple-800 mb-2">Pro Tip</h3>
              <p className="text-sm text-gray-700 mb-4">
                Connect with verified buyers to increase your chances of making successful deals.
              </p>
            </div>
          </div>

          {/* Buyers grid */}
          <div className="flex-1">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-gray-800">
                {totalCount} Software Buyers
              </h2>
            </div>

            {/* {loading == false && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                  {buyerData.slice(0, 3).map(renderExclusiveBuyerCard)}
                </div>
              </>
            )} */}

            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="flex flex-col items-center gap-2">
                  <svg className="animate-spin h-8 w-8 text-purple-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span className="text-sm text-gray-500">Loading buyers...</span>
                </div>
              </div>
            ) : buyerData.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {buyerData.map(buyer => (
                  <div
                    key={buyer.registered_user_id}
                    className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow border border-gray-100 overflow-hidden relative"
                  >
                    {/* Bookmark button - positioned at the top-right */}
                    <button
                      className={`absolute top-2 right-2 backdrop-blur-sm hover:bg-white z-10 rounded-full shadow-sm p-2 
                        ${buyer.is_bookmarked
                          ? 'bg-purple-100 text-purple-600'
                          : 'bg-white/80 text-gray-500'}`}
                      onClick={() => handleBookmarkToggle(buyer.registered_user_id, buyer.is_bookmarked)}
                      disabled={bookmarkLoading === buyer.registered_user_id}
                    >
                      {bookmarkLoading === buyer.registered_user_id ? (
                        <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      ) : buyer.is_bookmarked ? (
                        <BookmarkCheck className="h-5 w-5" />
                      ) : (
                        <BookmarkPlus className="h-5 w-5" />
                      )}
                    </button>

                    {/* Profile image and content wrapper */}
                    <div className="h-full flex flex-col">
                      {/* Profile image section */}
                      <div className="w-full bg-gray-50 pt-6 pb-3 px-6 flex justify-center">
                        <div className="h-24 w-24 rounded-full border-4 border-white shadow overflow-hidden flex items-center justify-center bg-purple-100 text-purple-700"
                        onClick={() => handleLoadProfile(buyer.slug, buyer.registered_user_id)}>
                          {buyer.profile_image ? (
                            <img
                              src={imageUrl + 'profileimages/' + buyer.profile_image}
                              alt={buyer.first_name}
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <span className="text-2xl font-medium">
                              {getInitials(buyer.first_name)}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Content section with flex column */}
                      <div className="p-6 flex-1 flex flex-col">
                        {/* Main content */}
                        <div className="flex-1">
                          <div className="text-center mb-4">
                            <div className="flex items-center justify-center gap-1.5 mb-1">
                              <h3 className="font-semibold text-lg text-gray-900">{buyer.first_name} {buyer.last_name}</h3>
                              {buyer.verified && (
                                <div className="inline-flex">
                                  {/* Using a custom verified badge display instead of component */}
                                  <div className="flex">
                                    {buyer.verified === 'email' && (
                                      <span className="bg-blue-100 text-blue-800 text-xs px-1.5 py-0.5 rounded-full">Email</span>
                                    )}
                                    {buyer.verified === 'linkedin' && (
                                      <span className="bg-blue-100 text-blue-800 text-xs px-1.5 py-0.5 rounded-full">LinkedIn</span>
                                    )}
                                    {buyer.verified === 'both' && (
                                      <div className="flex gap-1">
                                        <span className="bg-blue-100 text-blue-800 text-xs px-1.5 py-0.5 rounded-full">Verified</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>

                            <div className="text-gray-500 text-sm">
                              <span>{buyer.job_title} at</span>
                            </div>

                            <div className="text-gray-500 text-sm">
                              <Building className="h-3.5 w-3.5 mr-1" />
                              <span>{buyer.company_name}</span>
                            </div>

                            <div className="flex items-center justify-center text-gray-500 text-sm mt-1">

                              <span>{buyer.company_size}</span>
                              {buyer.country && (
                                <>
                                  <span className="mx-1.5 text-gray-300">•</span>
                                  <span>{buyer.country}</span>
                                </>
                              )}
                            </div>
                          </div>

                          <div className="mt-2">
                            <p className="text-gray-600 text-sm italic">
                              {buyer.biography || buyer.tagline}
                            </p>
                          </div>

                          <div className="mt-4">
                            <div className="flex flex-wrap gap-1.5 justify-center">
                              <span className="inline-flex items-center bg-purple-50 text-purple-700 hover:bg-purple-100 text-xs font-semibold px-2.5 py-0.5 rounded-full">
                                {buyer.industry}
                              </span>

                              {/* {buyer.interests.slice(0, 2).map((interest, idx) => (
                                <span key={idx} className="inline-flex items-center text-gray-600 bg-gray-50 border border-gray-200 hover:bg-gray-100 text-xs font-semibold px-2.5 py-0.5 rounded-full">
                                  {interest}
                                </span>
                              ))} */}

                              {/* {buyer.interests.length > 2 && (
                                <span className="inline-flex items-center text-gray-600 bg-gray-50 border border-gray-200 hover:bg-gray-100 text-xs font-semibold px-2.5 py-0.5 rounded-full">
                                  +{buyer.interests.length - 2}
                                </span>
                              )} */}
                            </div>
                          </div>
                        </div>

                        {/* Footer with connect button - always at bottom */}
                        <div className="mt-6 pt-4 border-t border-gray-100">
                          {buyer.request_sent ? (
                            <div className="text-sm text-gray-500 text-center py-1.5">
                              <Hourglass className="h-3.5 w-3.5 mr-1" /> Request sent
                            </div>
                          ) : (
                            <button
                              className="bg-black hover:bg-purple-700 text-white text-sm font-medium py-1.5 px-3 rounded-md w-full flex items-center justify-center"
                              onClick={() => handleConnectClick(buyer)}
                            >
                              {!hasSubscription && <Lock className="w-4 h-4 mr-2" />}
                              {hasSubscription ? 'Connect' : 'Connect'}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-white rounded-lg p-10 text-center">
                <UserRound className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900">No buyers found</h3>
                <p className="text-gray-500 mt-2">
                  Try adjusting your search or filters to find more buyers
                </p>
                <button
                  className="mt-4 border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 py-2 px-4 rounded-md"
                  onClick={resetFilters}
                >
                  Reset Filters
                </button>
              </div>
            )}
          </div>


        </div>

        <div className="mt-6">
          {renderPaginationButtons()}
          <div className="text-center mt-2 text-sm text-gray-500">
            Showing {Math.min(currentPage * ITEMS_PER_PAGE, totalCount)} of {totalCount} recommendations
          </div>
        </div>
      </div>

      {/* Connect Dialog */}
      {selectedBuyer && (
        <ConnectBuyerDialog
          open={connectDialogOpen}
          onOpenChange={setConnectDialogOpen}
          buyer={selectedBuyer}
          setBuyerData={setBuyerData}
        />
      )}

      <UpgradeDialog
        isOpen={isUpgradeDialogOpen}
        onClose={() => setIsUpgradeDialogOpen(false)}
      />

      {showUserProfile && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 overflow-hidden">
          <div className="h-full max-w-6xl mx-auto my-8 bg-white rounded-lg shadow-xl overflow-hidden">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex justify-between items-center">
              <button
                onClick={() => setShowUserProfile(false)}
                className="text-gray-500 hover:text-gray-700 focus:outline-none"
              >
                <X className="h-6 w-6" />
              </button>
              <div className="w-6"></div> {/* Spacer for alignment */}
            </div>
            <div className="p-0 overflow-y-auto max-h-[calc(100vh-8rem)]">
              <UserProfile
                user_slug={selectedSlug}
                user_id={selectedUserId}
                open={showUserProfile}
                onOpenChange={setShowUserProfile}
                setBuyerData={setBuyerData}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BuyerDirectory;